import React, { useState, useMemo } from 'react';
import { useVocalPacks } from '@hooks/useVocalPacks';
import VocalProfileCard from './VocalProfileCard';
import { Modal, Input, Button } from '../ui';
import { Search, Mic, Filter } from 'lucide-react';

interface VocalProfileBrowserProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (packKey: string) => void;
  selectedKey?: string | null;
  excludeKey?: string | null;
  contextLabel?: string;
}

/** Parse comma-separated styles into cleaned tags */
function parseStyleTags(styles: string): string[] {
  return styles
    .split(',')
    .map(s => s.trim().replace(/_/g, ' '))
    .filter(Boolean);
}

const VocalProfileBrowser: React.FC<VocalProfileBrowserProps> = ({
  isOpen,
  onClose,
  onSelect,
  selectedKey,
  excludeKey,
  contextLabel = 'Select Vocal Identity',
}) => {
  const { data: vocalPacks } = useVocalPacks();
  const [searchTerm, setSearchTerm] = useState('');
  const [styleFilter, setStyleFilter] = useState('');

  const packEntries = useMemo(() => Object.entries(vocalPacks), [vocalPacks]);

  // Derive top style categories from all packs
  const allStyleCategories = useMemo(() => {
    const countMap = new Map<string, number>();
    packEntries.forEach(([, pack]) => {
      parseStyleTags(pack.styles).forEach(tag => {
        countMap.set(tag, (countMap.get(tag) || 0) + 1);
      });
    });
    // Return styles that appear in 2+ packs, sorted by frequency then alphabetically
    return Array.from(countMap.entries())
      .filter(([, count]) => count >= 2)
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([tag]) => tag);
  }, [packEntries]);

  const filtered = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return packEntries.filter(([, pack]) => {
      // Search filter
      const matchesSearch = !term || [
        pack.name,
        pack.description,
        pack.styles.replace(/_/g, ' '),
        pack.voiceLead.replace(/_/g, ' '),
      ].some(field => field.toLowerCase().includes(term));

      // Style filter
      const matchesStyle = !styleFilter ||
        parseStyleTags(pack.styles).some(tag => tag === styleFilter);

      return matchesSearch && matchesStyle;
    });
  }, [packEntries, searchTerm, styleFilter]);

  const totalCount = packEntries.length;

  const handleSelect = (key: string) => {
    onSelect(key);
    onClose();
    setSearchTerm('');
    setStyleFilter('');
  };

  const handleClose = () => {
    onClose();
    setSearchTerm('');
    setStyleFilter('');
  };

  const headerContent = (
    <div className="flex items-center gap-3">
      <div className="p-2.5 bg-accent/20 rounded-xl text-accent">
        <Mic size={20} />
      </div>
      <div>
        <span className="text-xl font-black text-slate-50 uppercase tracking-tighter">
          {contextLabel}
        </span>
        <p className="text-[11px] text-slate-400 font-bold uppercase tracking-widest mt-1">
          Browse vocal identity profiles
        </p>
      </div>
    </div>
  );

  const footerContent = (
    <div className="flex justify-between items-center">
      <p className="text-[11px] text-slate-400 font-bold uppercase">
        Showing {filtered.length} of {totalCount} profiles
      </p>
      <Button variant="secondary" size="sm" onClick={handleClose}>
        Cancel
      </Button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title=""
      size="full"
      footer={footerContent}
      className="max-h-[85vh] flex flex-col rounded-3xl"
    >
      {/* Custom Header */}
      <div className="px-2 pb-5 border-b border-slate-800 -mt-2 mb-4">
        {headerContent}
      </div>

      {/* Search + Style Filter */}
      <div className="pb-5 border-b border-slate-800 mb-4 space-y-3">
        <Input
          placeholder="Search by name, description, styles, voice character..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          leftElement={<Search size={18} className="text-slate-400" />}
          autoFocus
          className="rounded-2xl py-3.5"
        />
        {allStyleCategories.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            <Filter size={14} className="text-slate-400" />
            <button
              onClick={() => setStyleFilter('')}
              className={`px-2.5 py-1 rounded-full text-[11px] font-black uppercase tracking-wider transition-all ${
                !styleFilter
                  ? 'bg-accent/20 text-accent border border-accent/30'
                  : 'bg-slate-800/50 text-slate-400 border border-slate-700/50 hover:text-slate-300'
              }`}
            >
              All
            </button>
            {allStyleCategories.map(style => (
              <button
                key={style}
                onClick={() => setStyleFilter(styleFilter === style ? '' : style)}
                className={`px-2.5 py-1 rounded-full text-[11px] font-black uppercase tracking-wider transition-all ${
                  styleFilter === style
                    ? 'bg-accent/20 text-accent border border-accent/30'
                    : 'bg-slate-800/50 text-slate-400 border border-slate-700/50 hover:text-slate-300'
                }`}
              >
                {style}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Card Grid */}
      <div className="flex-grow overflow-y-auto custom-scrollbar -mx-2 px-2">
        {filtered.length === 0 ? (
          <div className="h-64 flex flex-col items-center justify-center text-slate-700">
            <Mic size={48} className="opacity-20 mb-4" />
            <p className="font-black uppercase tracking-widest text-sm">No matching profiles</p>
            <p className="text-[11px] text-slate-400 mt-2">Try adjusting your search or style filter</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {filtered.map(([key, pack]) => (
              <VocalProfileCard
                key={key}
                pack={pack}
                packKey={key}
                variant="browser"
                isSelected={selectedKey === key}
                isExcluded={excludeKey === key}
                onClick={() => handleSelect(key)}
              />
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default VocalProfileBrowser;
